import React, { useState, useEffect, useCallback } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { X, Upload, Loader, CheckCircle, AlertCircle, Search, ChevronRight, ArrowLeft, Store } from 'lucide-react';

interface ShopifyProduct {
  id: string;
  title: string;
  handle: string;
  status: string;
  featuredImage: { url: string; altText: string | null } | null;
  images: { id: string; url: string; altText: string | null; width: number; height: number }[];
}

interface PushToShopifyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  imageUrl?: string;
  imageBase64?: string;
  defaultProductId?: string;
  defaultProductTitle?: string;
}

export function PushToShopifyModal({
  isOpen,
  onClose,
  onSuccess,
  imageUrl,
  imageBase64,
  defaultProductId,
  defaultProductTitle,
}: PushToShopifyModalProps) {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<ShopifyProduct | null>(null);
  const [replaceImageId, setReplaceImageId] = useState<string | null>(null);
  const [replaceImageUrl, setReplaceImageUrl] = useState<string | null>(null);
  const [loadingProduct, setLoadingProduct] = useState(false);

  const [step, setStep] = useState<'select-product' | 'configure'>('select-product');
  const session = useSession();
  const supabase = useSupabaseClient();

  useEffect(() => {
    if (isOpen) {
      setSuccess(false);
      setError(null);
      setPushing(false);
      setSelectedProduct(null);
      setReplaceImageId(null);
      setReplaceImageUrl(null);
      if (defaultProductId) {
        setStep('configure');
        fetchProductById(defaultProductId);
      } else {
        setLoading(true);
        setProducts([]);
        setStep('select-product');
        fetchProducts();
      }
    }
  }, [isOpen]);

  const fetchProducts = useCallback(async (searchQuery?: string) => {
    if (!session?.access_token) return;
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ limit: '30' });
      if (searchQuery) params.set('search', searchQuery);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-fetch-products?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch products');
      setProducts(data.products);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  async function fetchProductById(productId: string) {
    setLoadingProduct(true);
    try {
      const { data: { session: authSess } } = await supabase.auth.getSession();
      if (!authSess?.access_token) return;
      const gid = productId.startsWith('gid://') ? productId : `gid://shopify/Product/${productId}`;
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-fetch-products?productId=${encodeURIComponent(gid)}`,
        {
          headers: {
            'Authorization': `Bearer ${authSess.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        }
      );
      const data = await response.json();
      if (response.ok && data.products?.length > 0) {
        setSelectedProduct(data.products[0]);
      }
    } catch {
      // Product details unavailable - replace UI won't show, but push still works
    } finally {
      setLoadingProduct(false);
    }
  }

  useEffect(() => {
    if (isOpen && step === 'select-product') {
      fetchProducts(search);
    }
  }, [search, isOpen, step]);

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput);
  }

  function handleSelectProduct(product: ShopifyProduct) {
    setSelectedProduct(product);
    setStep('configure');
  }

  async function handlePush() {
    if (!selectedProduct && !defaultProductId) return;
    setPushing(true);
    setError(null);

    try {
      const { data: { session: authSess } } = await supabase.auth.getSession();
      if (!authSess?.access_token) throw new Error('Not authenticated');

      const payload: Record<string, unknown> = {
        productId: selectedProduct?.id || defaultProductId,
      };

      if (imageUrl) payload.imageUrl = imageUrl;
      if (imageBase64) payload.imageBase64 = imageBase64;
      if (replaceImageId) {
        payload.replaceImageId = replaceImageId;
        if (replaceImageUrl) payload.replaceImageUrl = replaceImageUrl;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-sync-image`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authSess.access_token}`,
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to push image');

      setSuccess(true);
      onSuccess?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setPushing(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center">
              <Store className="w-4 h-4 text-white" />
            </div>
            <h2 className="font-semibold text-gray-900">Push to Shopify</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Image Pushed Successfully</h3>
              <p className="text-sm text-gray-500 mb-6">
                Your image has been uploaded to {selectedProduct?.title || defaultProductTitle || 'your product'} on Shopify.
              </p>
              <button
                onClick={onClose}
                className="px-5 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
              >
                Done
              </button>
            </div>
          ) : step === 'select-product' ? (
            <>
              <form onSubmit={handleSearchSubmit} className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search products..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
              </form>

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg mb-4">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {loading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader className="w-6 h-6 animate-spin text-gray-400" />
                  <p className="text-sm text-gray-400">Loading products...</p>
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No products found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {products.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => handleSelectProduct(product)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-gray-400 hover:bg-gray-50 transition-all text-left"
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                        {product.featuredImage ? (
                          <img
                            src={product.featuredImage.url}
                            alt={product.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300">
                            <Store className="w-5 h-5" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{product.title}</p>
                        <p className="text-xs text-gray-500">{product.images.length} images</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <button
                onClick={() => { setLoading(true); setStep('select-product'); setSelectedProduct(null); setReplaceImageId(null); setReplaceImageUrl(null); fetchProducts(); }}
                className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Change product
              </button>

              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-gray-900">
                  {selectedProduct?.title || defaultProductTitle || 'Selected product'}
                </p>
              </div>

              {loadingProduct ? (
                <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
                  <Loader className="w-4 h-4 animate-spin" />
                  Loading product images...
                </div>
              ) : selectedProduct && selectedProduct.images.length > 0 ? (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Replace existing image (optional)
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    <button
                      onClick={() => { setReplaceImageId(null); setReplaceImageUrl(null); }}
                      className={`aspect-square rounded-lg border-2 flex items-center justify-center text-xs text-gray-500 ${
                        !replaceImageId ? 'border-gray-900 bg-gray-100' : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      Add New
                    </button>
                    {selectedProduct.images.map((img) => (
                      <button
                        key={img.id}
                        onClick={() => { setReplaceImageId(img.id); setReplaceImageUrl(img.url); }}
                        className={`aspect-square rounded-lg border-2 overflow-hidden ${
                          replaceImageId === img.id ? 'border-gray-900' : 'border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        <img src={img.url} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg mb-4">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {!success && step === 'configure' && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handlePush}
              disabled={pushing}
              className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {pushing ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {pushing ? 'Pushing to Shopify...' : 'Push Image'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
